import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Linkedin, ArrowUpRight, ThumbsUp, MessageCircle } from 'lucide-react';

// Block C (Sebastian 2026-06-15): zeigt die letzten LinkedIn-Posts als Trust-Signal.
// Daten kommen aus dem 6h-Cache (/api/public/linkedin/recent). Wenn leer/Fehler:
// rendert NICHTS (kein leerer Block, kein Fehler-Banner).

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'heute';
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 30) return `vor ${Math.floor(days / 7)} Wochen`;
  return d.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

export default function LinkedInFeedSection() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/public/linkedin/recent?limit=3`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (!cancelled) setPosts(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPosts([]); });
    return () => { cancelled = true; };
  }, []);

  // Nichts rendern wenn keine Posts (kein leerer Block)
  if (!posts || posts.length === 0) return null;

  return (
    <section className="bg-[#0a0a0a] text-white py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-3"
        >
          <Linkedin size={22} className="text-[#0a66c2]" />
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Aus dem Maschinenraum
          </p>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05] mb-12 max-w-3xl"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          Was wir gerade auf LinkedIn teilen
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {posts.map((p, i) => (
            <motion.a
              key={i}
              href={p.url || 'https://www.linkedin.com/company/enbet-consulting/'}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
              className="group bg-[#141414] border border-[#252525] hover:border-[#0a66c2] transition-colors p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/40 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {relativeTime(p.posted_at)}
                </span>
                <ArrowUpRight size={16} className="text-white/30 group-hover:text-[#0a66c2] transition-colors" />
              </div>
              <p className="text-white/80 text-sm leading-relaxed flex-1 line-clamp-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {p.text}
              </p>
              {(p.likes > 0 || p.comments > 0) && (
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#252525] text-white/40 text-xs">
                  {p.likes > 0 && <span className="flex items-center gap-1"><ThumbsUp size={12} /> {p.likes}</span>}
                  {p.comments > 0 && <span className="flex items-center gap-1"><MessageCircle size={12} /> {p.comments}</span>}
                </div>
              )}
            </motion.a>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 text-center"
        >
          <a
            href="https://www.linkedin.com/company/enbet-consulting/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#befa0a] hover:text-white transition-colors text-sm font-bold uppercase tracking-wider"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            <Linkedin size={16} /> Auf LinkedIn folgen <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
